import type {
  CreateInvoiceCommentMutationOptions,
  CreateInvoiceCommentVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { createInvoiceComment } from './createInvoiceComment';

/**
 * Mutation hook for adding a comment to an invoice timeline.
 *
 * The mutation settles only after the timeline query has been refetched, so
 * a caller awaiting `mutateAsync` can close its input once the new comment
 * is already in the list.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the creation.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/add_invoice_comment_invoices__invoice_id__comments_post
 */
export function useMutationCreateInvoiceComment(
  options?: CreateInvoiceCommentMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ comment, invoiceId }: CreateInvoiceCommentVariables) =>
      createInvoiceComment(invoiceId, { comment }),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.getTimeline(variables.invoiceId),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
